// Workload trends (Workload Intelligence Phase 3) — a "Last 30 days" strip of
// sparklines over the nightly snapshots: Active and Overdue counts, plus Load
// vs capacity when the population had capacity configured. Inline SVG only (no
// chart library), theme tokens via `currentColor`. Numbers describe WORK; the
// only percentage anywhere stays the configured-capacity utilization (Phase 2)
// — this strip shows units, never a ratio. Days without a snapshot are simply
// absent from the series and are positioned by date, so gaps are honest.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { useWorkloadTrends } from '@/features/work/api/workloadHooks';
import type { WorkloadTrendDay, WorkloadTrends } from '@/services/workloadService';

type T = (key: string, opts?: Record<string, unknown>) => string;

export interface SparklineProps {
  /** Primary series, oldest → newest. Empty → an empty, labelled SVG. */
  values: number[];
  /** Optional second series on the SAME scale (dashed, muted) — e.g. capacity. */
  compare?: Array<number | null>;
  /** Horizontal positions in [0, 1] per value; defaults to even spacing. */
  positions?: number[];
  /** Accessible name; the label reads "<label>: <first> → <last>". */
  label: string;
  /** Tailwind text-colour token for the primary line (currentColor). */
  className?: string;
  width?: number;
  height?: number;
  testId?: string;
}

const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10));

/** Builds an SVG polyline "x,y x,y" for one series on a shared y-scale. */
export function sparklinePoints(values: Array<number | null>, positions: number[], min: number, max: number, w: number, h: number): string {
  const pad = 2;
  const span = max - min || 1;
  return values
    .map((v, i) => {
      if (v === null || v === undefined || !Number.isFinite(v)) return null;
      const x = positions.length === values.length ? positions[i] : (values.length > 1 ? i / (values.length - 1) : 0.5);
      const px = pad + x * (w - pad * 2);
      const py = pad + (1 - (v - min) / span) * (h - pad * 2);
      return `${px.toFixed(1)},${py.toFixed(1)}`;
    })
    .filter(Boolean)
    .join(' ');
}

export const Sparkline: React.FC<SparklineProps> = ({
  values, compare, positions, label, className = 'text-primary-500', width = 120, height = 32, testId,
}) => {
  const { t } = useTranslation('work');
  const w = Math.min(120, width);
  const h = Math.min(32, height);
  const all = [...values, ...((compare || []).filter((v): v is number => typeof v === 'number'))];
  const min = all.length ? Math.min(0, ...all) : 0;
  const max = all.length ? Math.max(...all) : 0;
  const pos = positions && positions.length === values.length ? positions : values.map((_, i) => (values.length > 1 ? i / (values.length - 1) : 0.5));
  const aria = values.length
    ? t('sparkline_aria', { defaultValue: '{{label}}: {{first}} → {{last}}', label, first: fmt(values[0]), last: fmt(values[values.length - 1]) })
    : t('sparkline_empty_aria', { defaultValue: '{{label}}: no data', label });
  const main = sparklinePoints(values, pos, min, max, w, h);
  const cmp = compare && compare.length ? sparklinePoints(compare, pos, min, max, w, h) : '';
  const lastIdx = values.length - 1;
  const lastPoint = values.length ? sparklinePoints([values[lastIdx]], [pos[lastIdx]], min, max, w, h) : '';
  const [lx, ly] = lastPoint ? lastPoint.split(',').map(Number) : [0, 0];
  return (
    <svg role="img" aria-label={aria as string} width={w} height={h} viewBox={`0 0 ${w} ${h}`}
      className={`shrink-0 ${className}`} data-testid={testId} data-points={values.length}>
      {cmp && <polyline points={cmp} fill="none" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" className="text-muted-foreground" opacity={0.8} />}
      {main && values.length > 1 && <polyline points={main} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />}
      {lastPoint && <circle cx={lx} cy={ly} r={2} fill="currentColor" />}
    </svg>
  );
};

/** Day offset (0..1) of each snapshot inside [from, to]; falls back to even spacing. */
export function trendPositions(days: WorkloadTrendDay[], from: string, to: string): number[] {
  const a = Date.parse(from), b = Date.parse(to);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return days.map((_, i) => (days.length > 1 ? i / (days.length - 1) : 0.5));
  return days.map((d) => {
    const x = Date.parse(d.day);
    return Number.isFinite(x) ? Math.max(0, Math.min(1, (x - a) / (b - a))) : 0;
  });
}

const Metric: React.FC<{ label: string; current: string; children: React.ReactNode; testId: string }> = ({ label, current, children, testId }) => (
  <div className="flex items-center gap-3 rounded-[10px] border border-border px-3 py-2 min-w-[200px]" data-testid={testId}>
    <div className="min-w-0">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="text-sm tabular-nums text-foreground" data-testid={`${testId}-current`}>{current}</p>
    </div>
    {children}
  </div>
);

/** Pure renderer of one trends payload (the strip); `TrendStrip` fetches. */
export const TrendStripView: React.FC<{ data: WorkloadTrends; showCapacity?: boolean; testId?: string }> = ({ data, showCapacity = true, testId = 'trend-strip' }) => {
  const { t } = useTranslation('work');
  const tt = t as unknown as T;
  const days = data.days;
  const empty = !data.snapshots_available || days.length === 0;
  const pos = trendPositions(days, data.from, data.to);
  const last = days[days.length - 1];
  const hasCapacity = showCapacity && days.some((d) => typeof d.capacity_units === 'number');
  // Compare like with like: the load of the people whose capacity is configured
  // (older payloads without the field fall back to the population load).
  const configuredLoad = (d: WorkloadTrendDay) => (typeof d.configured_load_units === 'number' ? d.configured_load_units : d.load_units);
  return (
    <section className="space-y-2" data-testid={testId} aria-label={tt('trend_last_30', { defaultValue: 'Last 30 days' })}>
      <h3 className="text-xs font-medium text-charcoal">{t('trend_last_30', { defaultValue: 'Last 30 days' })}</h3>
      {empty ? (
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-empty`}>{t('trend_empty', { defaultValue: 'Trends start after the first nightly snapshot.' })}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Metric label={tt('tile_active', { defaultValue: 'Active' })} current={fmt(last.active)} testId={`${testId}-active`}>
            <Sparkline values={days.map((d) => d.active)} positions={pos} label={tt('tile_active', { defaultValue: 'Active' })} className="text-primary-500" testId={`${testId}-active-spark`} />
          </Metric>
          <Metric label={tt('tile_overdue', { defaultValue: 'Overdue' })} current={fmt(last.overdue)} testId={`${testId}-overdue`}>
            <Sparkline values={days.map((d) => d.overdue)} positions={pos} label={tt('tile_overdue', { defaultValue: 'Overdue' })} className="text-danger" testId={`${testId}-overdue-spark`} />
          </Metric>
          {hasCapacity && (
            <Metric label={tt('trend_load_vs_capacity', { defaultValue: 'Load vs capacity' })} testId={`${testId}-load`}
              current={typeof last.capacity_units === 'number'
                ? tt('trend_load_now', { defaultValue: '{{load}} of {{units}} units planned', load: fmt(configuredLoad(last)), units: fmt(last.capacity_units) })
                : tt('trend_load_only', { defaultValue: '{{load}} units planned', load: fmt(last.load_units) })}>
              <Sparkline values={days.map((d) => (typeof d.capacity_units === 'number' ? configuredLoad(d) : d.load_units))} compare={days.map((d) => d.capacity_units)} positions={pos}
                label={tt('trend_load_vs_capacity', { defaultValue: 'Load vs capacity' })} className="text-primary-500" testId={`${testId}-load-spark`} />
            </Metric>
          )}
        </div>
      )}
    </section>
  );
};

/** Fetching wrapper: one person (`employeeId`) or the viewer's population
 * (optionally one department). Errors render inline (the backend message),
 * never a toast — the surrounding view already toasts its own failures. */
export const TrendStrip: React.FC<{
  employeeId?: string | null;
  departmentId?: string;
  showCapacity?: boolean;
  refreshKey?: number;
  testId?: string;
}> = ({ employeeId, departmentId, showCapacity = true, refreshKey = 0, testId = 'trend-strip' }) => {
  const { t } = useTranslation('work');
  const { data, loading, error } = useWorkloadTrends(
    { employee_id: employeeId || undefined, department_id: departmentId || undefined, days: 30 }, true, refreshKey,
  );
  if (loading && !data) {
    return (
      <p className="text-xs text-muted-foreground flex items-center gap-2" role="status" data-testid={`${testId}-loading`}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('trend_last_30', { defaultValue: 'Last 30 days' })}
      </p>
    );
  }
  if (error) {
    return (
      <p className="text-xs text-muted-foreground" data-testid={`${testId}-error`}>
        {getApiErrorMessage(error, t('trend_load_failed', { defaultValue: 'Could not load trends.' }))}
      </p>
    );
  }
  if (!data) return null;
  return <TrendStripView data={data} showCapacity={showCapacity} testId={testId} />;
};

export default TrendStrip;
